import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { ActionDialog, ReasonModal } from '../../components/dialogs';
import { HistorySection, hubTransferHistory } from '../../components/history';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  linkCls,
  LoadError,
  MappedBadge,
  metaCls,
  nameCls,
  numCls,
  Section,
  StatusBadge,
} from '../../components/ui';
import { fmtDate, fmtDateTime, fmtQty } from '../../format';
import { useLoad } from '../../hooks/useLoad';
import { WAREHOUSE_PATHS } from '../../paths';
import {
  HUB_TRANSFER_STATUS,
  hubTransferNextStep,
  OUTBOUND_CANCELLABLE,
  OUTBOUND_STATUS,
  RECEIPT_STATUS,
} from '../../status';
import type {
  HubTransferDetail,
  HubTransferLine,
  OutboundOrderStatus,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';

type BusyKind = 'first' | 'approve' | 'reject' | 'cancel';
/** How an action ended; `handled` = the caller showed the failure itself. */
type Outcome = 'done' | 'failed' | 'handled' | 'busy';

const LINE_COLUMNS: TableDataColumn<HubTransferLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Nama produk',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
  {
    key: 'qtyAvailable',
    title: 'Tersedia saat request',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyAvailable)}</span>,
  },
  {
    key: 'qty',
    title: 'Qty transfer',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
];

const HubTransferDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getHubTransfer(id, { signal }),
    [id, refreshToken]
  );
  const [busy, setBusy] = useState<BusyKind | null>(null);
  const busyRef = useRef(false);
  const [firstApproving, setFirstApproving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.hubTransfer}
          backLabel="Transfer warehouse"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Transfer tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  const t = detail.data;

  /**
   * One action at a time. The answer is the document's new state; a failure
   * is a toast (and a reload when the status moved under us).
   */
  const run = async (
    kind: BusyKind,
    call: () => Promise<HubTransferDetail>,
    success: string,
    onFail?: (err: unknown) => boolean
  ): Promise<Outcome> => {
    if (busyRef.current) return 'busy';
    busyRef.current = true;
    setBusy(kind);
    try {
      const next = await call();
      detail.setData(next);
      toast.success(success, { description: t.code });
      return 'done';
    } catch (err) {
      if (onFail?.(err)) return 'handled';
      const e = parseWarehouseError(err);
      toast.error(
        e.status === 422 && e.fieldErrors.length
          ? e.fieldErrors[0].message
          : e.message
      );
      // Someone else acted first: show the document as it is now.
      if (e.status === 409) detail.reload();
      return 'failed';
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const firstApprove = async () => {
    const outcome = await run(
      'first',
      () => warehouseApi.firstApproveHubTransfer(t.id),
      'Disetujui hub asal'
    );
    // Leave the dialog whatever the outcome: a failure is a toast on the page.
    if (outcome !== 'busy') setFirstApproving(false);
  };
  const approve = async () => {
    const outcome = await run(
      'approve',
      () => warehouseApi.approveHubTransfer(t.id),
      'Disetujui hub tujuan, outbound order dibuat'
    );
    if (outcome !== 'busy') setApproving(false);
  };
  const reject = async (reason: string) => {
    setRejectError(null);
    const outcome = await run(
      'reject',
      () => warehouseApi.rejectHubTransfer(t.id, reason),
      'Transfer ditolak',
      (err) => {
        const e = parseWarehouseError(err);
        if (e.status !== 422) return false;
        setRejectError(e.fieldErrors[0]?.message ?? e.message);
        return true;
      }
    );
    if (outcome === 'done' || outcome === 'failed') setRejecting(false);
  };

  /**
   * After approval the transfer IS its outbound order: cancelling it there
   * cancels the transfer (its status follows) and releases the reservation.
   * Only while that order is still in process (Open / Preparing / Ready to
   * pick up); once shipped there is no cancel here.
   */
  const order = t.outboundOrder;
  const canCancel =
    !!order &&
    OUTBOUND_CANCELLABLE.includes(order.status as OutboundOrderStatus);
  const cancelTransfer = async (reason: string) => {
    if (!order || busyRef.current) return;
    busyRef.current = true;
    setBusy('cancel');
    setCancelError(null);
    try {
      await warehouseApi.cancelOutboundOrder(order.id, reason);
      detail.setData(await warehouseApi.getHubTransfer(t.id));
      toast.success('Transfer dibatalkan', {
        description: `${order.code} dibatalkan, reserve stok dilepas.`,
      });
      setCancelling(false);
    } catch (err) {
      const e = parseWarehouseError(err);
      const reasonErr = e.fieldErrors.find((f) => f.field === 'reason');
      if (reasonErr) setCancelError(reasonErr.message);
      else if (e.status === 409) setCancelError(e.message);
      else {
        toast.error(e.message);
        setCancelling(false);
      }
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const idle = busy === null;
  const sourceName = t.hub?.name ?? `Hub #${t.hubID}`;
  const destName = t.destinationHub?.name ?? `Hub #${t.destinationHubID}`;
  const clientName = t.client?.name ?? `Client #${t.clientID}`;
  const pending = t.status === 'REQUESTED' || t.status === 'FIRST_APPROVED';
  const nextStep = hubTransferNextStep(t.status, t.outboundOrder?.code);
  const coords =
    t.destinationLatitude !== null && t.destinationLongitude !== null
      ? `${t.destinationLatitude}, ${t.destinationLongitude}`
      : null;

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.hubTransfer}
        backLabel="Transfer warehouse"
        meta={
          <>
            Diajukan {fmtDateTime(t.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {t.createdBy}
            </span>
          </>
        }
        title={t.code}
        badges={<StatusBadge meta={HUB_TRANSFER_STATUS[t.status]} />}
        actions={
          pending ? (
            <>
              <Button
                tone="destructive"
                style="ghost"
                data-testid="hub-transfer-reject"
                disabled={!idle}
                onClick={() => {
                  setRejectError(null);
                  setRejecting(true);
                }}
                leftIcon={<i className="ri-close-circle-line text-[14px]" />}
              >
                Tolak
              </Button>
              {t.status === 'REQUESTED' ? (
                <Button
                  data-testid="hub-transfer-first-approve"
                  loading={busy === 'first'}
                  disabled={!idle}
                  onClick={() => setFirstApproving(true)}
                  leftIcon={<i className="ri-check-line text-[14px]" />}
                >
                  Approve (hub asal)
                </Button>
              ) : (
                <Button
                  data-testid="hub-transfer-approve"
                  loading={busy === 'approve'}
                  disabled={!idle}
                  onClick={() => setApproving(true)}
                  leftIcon={<i className="ri-check-double-line text-[14px]" />}
                >
                  Approve (hub tujuan)
                </Button>
              )}
            </>
          ) : canCancel ? (
            <Button
              tone="destructive"
              style="stroke"
              data-testid="hub-transfer-cancel"
              disabled={!idle}
              onClick={() => {
                setCancelError(null);
                setCancelling(true);
              }}
              leftIcon={<i className="ri-close-circle-line text-[14px]" />}
            >
              Batalkan transfer
            </Button>
          ) : undefined
        }
      />

      {nextStep && (
        <Alert
          status="information"
          appearance="stroke"
          size="sm"
          title={nextStep}
          data-testid="hub-transfer-next-step"
        />
      )}
      {t.status === 'CANCELLED' && (
        <Alert
          status="warning"
          appearance="stroke"
          size="sm"
          title="Outbound order transfer ini dibatalkan setelah disetujui."
          data-testid="hub-transfer-cancelled"
        />
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section
            title="Daftar produk"
            description={`${t.lineCount} baris · ${fmtQty(t.totalQty)} total qty`}
          >
            <div data-testid="hub-transfer-lines">
              <TableData size="sm" data={t.lines} columns={LINE_COLUMNS} />
              <div className="flex items-center justify-between border-t border-stroke-soft-200 px-3 pt-2.5 text-[13px]">
                <span className="text-text-sub-600">Total qty transfer</span>
                <span
                  className="font-mono font-semibold tabular-nums text-text-strong-950"
                  data-testid="hub-transfer-total"
                >
                  {fmtQty(t.totalQty)}
                </span>
              </div>
            </div>
          </Section>
          <div data-testid="hub-transfer-history">
            <HistorySection entries={hubTransferHistory(t.events)} />
          </div>
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Informasi transfer">
            <KV label="Client" value={clientName} />
            <KV label="Hub asal" value={sourceName} />
            <KV
              label="Hub tujuan"
              value={destName}
              sub={t.destinationAddress || undefined}
            />
            <KV
              label="Penerima"
              value={t.recipientName}
              sub={t.recipientPhone || undefined}
            />
            <KV
              label="Koordinat tujuan"
              value={coords && <span className="font-mono">{coords}</span>}
            />
            <KV label="Catatan tujuan" value={t.destinationNotes} />
            <KV label="Estimasi kirim" value={fmtDate(t.expectedDeliveryDate)} />
            <KV label="Alasan transfer" value={t.notes} />
            <KV
              label="Pemohon"
              value={t.createdBy}
              sub={fmtDateTime(t.createdAt)}
            />
            {t.firstApprovedAt && (
              <KV
                label="Disetujui hub asal"
                value={t.firstApprovedBy}
                sub={fmtDateTime(t.firstApprovedAt)}
              />
            )}
            {t.approvedAt && (
              <KV
                label="Disetujui hub tujuan"
                value={t.approvedBy}
                sub={fmtDateTime(t.approvedAt)}
              />
            )}
            {t.rejectedAt && (
              <KV
                label="Ditolak oleh"
                value={t.rejectedBy}
                sub={fmtDateTime(t.rejectedAt)}
              />
            )}
            {t.status === 'REJECTED' && (
              <KV label="Alasan penolakan" value={t.rejectReason} />
            )}
          </Section>

          <Section title="Dokumen terkait">
            <div className="flex flex-col" data-testid="hub-transfer-related">
              <div className="flex items-start justify-between gap-4 border-b border-stroke-soft-200 py-2.5">
                <span className="text-[13px] text-text-sub-600">
                  Outbound order
                </span>
                {t.outboundOrder ? (
                  <span className="flex flex-col items-end gap-1">
                    <Link
                      to={`${WAREHOUSE_PATHS.outboundRequest}/${t.outboundOrder.id}`}
                      className={linkCls}
                      data-testid="hub-transfer-order-link"
                    >
                      {t.outboundOrder.code}
                    </Link>
                    <MappedBadge
                      map={OUTBOUND_STATUS}
                      value={t.outboundOrder.status}
                    />
                  </span>
                ) : (
                  <span className="text-right text-[12px] text-text-soft-400">
                    Belum ada, terbit saat approval hub tujuan
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between gap-4 py-2.5">
                <span className="text-[13px] text-text-sub-600">
                  Inbound request
                </span>
                {t.transferPurchaseOrder ? (
                  <span className="flex flex-col items-end gap-1">
                    <Link
                      to={`${WAREHOUSE_PATHS.inboundRequest}/${t.transferPurchaseOrder.id}`}
                      className={linkCls}
                      data-testid="hub-transfer-po-link"
                    >
                      {t.transferPurchaseOrder.code}
                    </Link>
                    {t.transferPurchaseOrder.receiptStatus && (
                      <MappedBadge
                        map={RECEIPT_STATUS}
                        value={t.transferPurchaseOrder.receiptStatus}
                      />
                    )}
                  </span>
                ) : (
                  <span className="text-right text-[12px] text-text-soft-400">
                    Belum ada, terbit saat pengiriman (shipping)
                  </span>
                )}
              </div>
            </div>
          </Section>
        </div>
      </div>

      <ActionDialog
        open={firstApproving}
        title="Approve (hub asal)"
        subtitle={t.code}
        description={`${sourceName} menyetujui transfer ke ${destName}. Setelah ini transfer menunggu approval hub tujuan.`}
        confirmLabel="Approve (hub asal)"
        busy={busy === 'first'}
        testId="hub-transfer-first-approve-dialog"
        onConfirm={() => void firstApprove()}
        onClose={() => setFirstApproving(false)}
      />
      <ActionDialog
        open={approving}
        title="Approve (hub tujuan)"
        subtitle={t.code}
        description="Outbound order TRANSFER akan dibuat di hub asal; stok di-reserve saat picking order dibuat."
        confirmLabel="Approve (hub tujuan)"
        busy={busy === 'approve'}
        testId="hub-transfer-approve-dialog"
        onConfirm={() => void approve()}
        onClose={() => setApproving(false)}
      />
      <ReasonModal
        open={rejecting}
        title="Tolak transfer"
        subtitle={t.code}
        notice="Transfer yang ditolak tidak dapat diproses lagi."
        confirmLabel="Tolak transfer"
        placeholder="Mis. stok dibutuhkan untuk order lain"
        busy={busy === 'reject'}
        error={rejectError}
        testId="hub-transfer-reject-dialog"
        onConfirm={(reason) => void reject(reason)}
        onClose={() => setRejecting(false)}
      />
      <ReasonModal
        open={cancelling}
        title="Batalkan transfer"
        subtitle={order ? `${t.code} · ${order.code}` : t.code}
        notice="Outbound order transfer dibatalkan dan reserve stok di hub asal dilepas."
        confirmLabel="Batalkan transfer"
        placeholder="Mis. hub tujuan tidak jadi butuh"
        busy={busy === 'cancel'}
        error={cancelError}
        testId="hub-transfer-cancel-dialog"
        onConfirm={(reason) => void cancelTransfer(reason)}
        onClose={() => setCancelling(false)}
      />
    </div>
  );
};

export default HubTransferDetailPage;
